/**
 * `[#H1-2]` image references inside free text.
 *
 * `RefText` renders the text with each reference as a link. The
 * `LinkedTextArea` next to it is the editable form: it shows RefText
 * until you click into it, then swaps to a real textarea so the raw
 * `[#H1-2]` is only visible while you are actually editing it.
 *
 * Deliberately not markdown-shaped. The body is free text and the
 * only thing worth linkifying is the reference the "+#" button
 * writes, so the parser is one regex and a split.
 */
import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type Ref,
} from "react";
import { useAutosizeTextarea } from "@/hooks/useAutosizeTextarea";
import styles from "./RefText.module.css";

/** `[#H1-2]` — a label is at most 32 chars and never contains a
 *  newline or a closing bracket, which is what keeps this from
 *  running away across paragraphs. */
const REF_TOKEN = /\[#([^\]\n]{1,32})\]/g;

type Part =
  | { kind: "text"; text: string; at: number }
  | { kind: "ref"; label: string; at: number; raw: string };

function parseRefs(text: string): Part[] {
  const parts: Part[] = [];
  let last = 0;
  for (const m of text.matchAll(REF_TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) {
      parts.push({ kind: "text", text: text.slice(last, at), at: last });
    }
    parts.push({ kind: "ref", label: m[1], at, raw: m[0] });
    last = at + m[0].length;
  }
  if (last < text.length) {
    parts.push({ kind: "text", text: text.slice(last), at: last });
  }
  return parts;
}

export function RefText({
  text,
  onRefClick,
}: {
  text: string;
  /** Omit to render references as plain styled text — used where the
   *  surrounding element owns the click, like the journal's collapsed
   *  quote, which expands the row. */
  onRefClick?: (label: string) => void;
}) {
  return (
    <>
      {parseRefs(text).map((part) =>
        part.kind === "text" ? (
          // Every run carries its source offset so a click in the
          // rendered view can be mapped back to a caret position in
          // the raw text — see sourceOffsetFromPoint.
          <span key={part.at} data-src-at={part.at}>
            {part.text}
          </span>
        ) : onRefClick ? (
          <button
            key={part.at}
            type="button"
            className={styles.refLink}
            onClick={(e) => {
              e.stopPropagation();
              onRefClick(part.label);
            }}
            title={`Open image #${part.label}`}
          >
            #{part.label}
          </button>
        ) : (
          <span key={part.at} className={styles.refPlain}>
            #{part.label}
          </span>
        ),
      )}
    </>
  );
}

export interface LinkedTextAreaHandle {
  /** Insert at the caret, opening the editor first if it is closed.
   *  Used by the "+#" button on a thumbnail. */
  insertAtCaret: (text: string) => void;
}

/** Map a click in the rendered view back to an offset in the raw
 *  text, so clicking into the middle of a paragraph puts the caret
 *  where you clicked rather than at the end.
 *
 *  Works off the `data-src-at` markers RefText emits: find the text
 *  run that was clicked, then add the offset within it. Runs are
 *  verbatim slices of the source, so that inner offset maps 1:1 —
 *  which is exactly why references are not part of a run. */
function sourceOffsetFromPoint(
  container: HTMLElement,
  x: number,
  y: number,
): number | null {
  const doc = container.ownerDocument;
  let node: Node | null = null;
  let offset = 0;

  // caretRangeFromPoint is Blink/WebKit, caretPositionFromPoint is
  // the standard one Firefox implements. Neither is universal.
  const legacy = (
    doc as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
    }
  ).caretRangeFromPoint;
  if (typeof legacy === "function") {
    const range = legacy.call(doc, x, y);
    if (range) {
      node = range.startContainer;
      offset = range.startOffset;
    }
  } else if (typeof doc.caretPositionFromPoint === "function") {
    const pos = doc.caretPositionFromPoint(x, y);
    if (pos) {
      node = pos.offsetNode;
      offset = pos.offset;
    }
  }
  if (!node) return null;

  const el =
    node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  const run = el?.closest?.("[data-src-at]") as HTMLElement | null;
  if (!run || !container.contains(run)) return null;
  const base = Number(run.dataset.srcAt);
  if (!Number.isFinite(base)) return null;
  return base + offset;
}

export function LinkedTextArea({
  value,
  onChange,
  onCommit,
  onRefClick,
  placeholder,
  className,
  handleRef,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Blur and Ctrl/Cmd+Enter. The caller decides whether that is a
   *  save; this only reports the moment. */
  onCommit: () => void;
  onRefClick: (label: string) => void;
  placeholder: string;
  className: string;
  handleRef?: Ref<LinkedTextAreaHandle>;
}) {
  const [editing, setEditing] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const viewRef = useRef<HTMLDivElement | null>(null);
  // Where to put the caret once the textarea exists. Set by a click
  // into the rendered view, or by an insert.
  const pendingCaret = useRef<number | null>(null);
  const resizeRef = useAutosizeTextarea(value);

  // Memoised on purpose. The autosize hook keeps its element in
  // state, so a fresh callback each render would make React detach
  // (null) and re-attach it every time — two setState calls per
  // render for no reason. It settles rather than looping, because
  // the second call restores the same element, but it is still a
  // wasted render cycle on every keystroke.
  const setEl = useCallback(
    (el: HTMLTextAreaElement | null) => {
      taRef.current = el;
      resizeRef(el);
    },
    [resizeRef],
  );

  // Placing the caret has to wait for the textarea to exist AND to
  // hold the new value, so it runs after the commit that does both.
  useLayoutEffect(() => {
    if (!editing) return;
    const el = taRef.current;
    const at = pendingCaret.current;
    if (!el || at == null) return;
    pendingCaret.current = null;
    el.focus();
    const clamped = Math.min(Math.max(at, 0), el.value.length);
    el.setSelectionRange(clamped, clamped);
  }, [editing, value]);

  useImperativeHandle(handleRef, () => ({
    insertAtCaret: (text: string) => {
      const el = taRef.current;
      if (!editing || !el) {
        // Nothing is focused, so there is no caret to insert at —
        // append, and open the editor so typing carries on straight
        // after the token.
        onChange(value + text);
        pendingCaret.current = value.length + text.length;
        setEditing(true);
        return;
      }
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? start;
      onChange(value.slice(0, start) + text + value.slice(end));
      pendingCaret.current = start + text.length;
    },
  }));

  if (editing) {
    return (
      <div className={styles.wrap}>
        {/* Highlight layer. Sits BEHIND the textarea and paints a tint
            under each reference; the glyphs you actually read are the
            textarea's own, in front.

            The obvious way round — transparent textarea text, glyphs
            rendered here — would let the tokens be properly coloured,
            but selecting text then highlights invisible glyphs. A
            note body you can't select is a worse regression than a
            token whose letters aren't blue.

            It carries the caller's class so every metric that decides
            where a character lands (font, size, line-height, padding,
            border width, letter-spacing) is identical; anything less
            and the tint drifts off the word. For the same reason its
            text is character-for-character the raw value, brackets
            included — see `raw` in parseRefs. */}
        <div className={`${className} ${styles.backdrop}`} aria-hidden="true">
          {parseRefs(value).map((part) =>
            part.kind === "text" ? (
              <span key={part.at}>{part.text}</span>
            ) : (
              <span key={part.at} className={styles.hlToken}>
                {part.raw}
              </span>
            ),
          )}
        </div>
        <textarea
          ref={setEl}
          className={`${className} ${styles.input}`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            setEditing(false);
            onCommit();
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              setEditing(false);
              onCommit();
            }
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={viewRef}
      className={`${className} ${styles.view} ${value ? "" : styles.viewEmpty}`}
      onClick={(e) => {
        // A click that landed on a reference already opened the
        // image and stopped propagating; anything else means "let me
        // type here".
        const el = viewRef.current;
        pendingCaret.current = el
          ? sourceOffsetFromPoint(el, e.clientX, e.clientY)
          : null;
        if (pendingCaret.current == null) pendingCaret.current = value.length;
        setEditing(true);
      }}
      title="Click to edit"
    >
      {value ? <RefText text={value} onRefClick={onRefClick} /> : placeholder}
    </div>
  );
}
